import cn from "classnames";
import { Input, InputLabel } from "../shared";

export type FilterOption<T> = {
    id: string;
    label: string;
    filterFn: (data: T, value: any) => boolean;
    type: "text" | "select" | "date" | "daterange" | "number";
    options?: { value: string; label: string }[];
    value?: any;
};

export type FilterProps<T> = {
    filters: FilterOption<T>[];
    onFilterChange: (filters: FilterOption<T>[]) => void;
    className?: string;
    labelSize?: "sm" | "md" | "lg";
    boldLabels?: boolean;
};

export function Filter<T>({ filters, onFilterChange, className, labelSize = "md", boldLabels = true }: FilterProps<T>): JSX.Element {
    const handleFilterChange = (id: string, value: any) => {
        const updatedFilters = filters.map((filter) => (filter.id === id ? { ...filter, value } : filter));
        onFilterChange(updatedFilters);
    };

    const handleDateRangeChange = (id: string, field: "from" | "to", value: string) => {
        const filter = filters.find((f) => f.id === id);
        const currentValue = filter?.value || {};

        const newValue = {
            ...currentValue,
            [field]: value || undefined,
        };
        if (!newValue.from && !newValue.to) {
            handleFilterChange(id, undefined);
        } else {
            handleFilterChange(id, newValue);
        }
    };

    const clearFilters = () => {
        const clearedFilters = filters.map((filter) => ({ ...filter, value: undefined }));
        onFilterChange(clearedFilters);
    };

    const activeFilterCount = filters.filter((f) => {
        if (f.type === "daterange") {
            return f.value && (f.value.from || f.value.to);
        }
        return f.value !== undefined && f.value !== "";
    }).length;

    return (
        <div className={cn("mb-4", className)}>
            {/* Phones get one stacked column of full-width fields with left-aligned labels; the
                centred-label row that suits a desk collapsed into inputs of unrelated widths with
                their labels floating over empty space. */}
            <div className="grid grid-cols-1 gap-3 sm:flex sm:flex-wrap sm:items-end">
                {filters.map((filter) => (
                    <div key={filter.id} className="flex flex-col items-stretch sm:items-center">
                        {filter.type === "text" && (
                            <div className="flex flex-col items-stretch sm:items-center">
                                <InputLabel size={labelSize} bold={boldLabels}>
                                    {filter.label}
                                </InputLabel>
                                <Input value={filter.value || ""} onChange={(e) => handleFilterChange(filter.id, e.target.value)} placeholder={`Filter by ${filter.label.toLowerCase()}`} className="w-full sm:w-auto sm:min-w-[180px]" />
                            </div>
                        )}

                        {filter.type === "select" && (
                            <div className="flex flex-col items-stretch sm:items-center">
                                <InputLabel size={labelSize} bold={boldLabels}>
                                    {filter.label}
                                </InputLabel>
                                <select value={filter.value || ""} onChange={(e) => handleFilterChange(filter.id, e.target.value)} className="border rounded px-3 py-2 w-full sm:w-auto sm:min-w-[180px]">
                                    <option value="">All</option>
                                    {filter.options?.map((option) => (
                                        <option key={option.value} value={option.value}>
                                            {option.label}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        )}

                        {filter.type === "number" && (
                            <div className="flex flex-col items-stretch sm:items-center">
                                <InputLabel size={labelSize} bold={boldLabels}>
                                    {filter.label}
                                </InputLabel>
                                <Input
                                    type="number"
                                    value={filter.value !== undefined ? filter.value : ""}
                                    onChange={(e) => {
                                        const value = e.target.value;
                                        if (value === "") {
                                            handleFilterChange(filter.id, undefined);
                                        } else {
                                            handleFilterChange(filter.id, Number(value));
                                        }
                                    }}
                                    placeholder={`Filter by ${filter.label.toLowerCase()}`}
                                    className="w-full sm:w-auto sm:min-w-[180px]"
                                />
                            </div>
                        )}

                        {filter.type === "date" && (
                            <div className="flex flex-col items-stretch sm:items-center">
                                <InputLabel size={labelSize} bold={boldLabels}>
                                    {filter.label}
                                </InputLabel>
                                <Input
                                    type="date"
                                    value={filter.value !== undefined ? filter.value : ""}
                                    onChange={(e) => {
                                        const value = e.target.value;
                                        handleFilterChange(filter.id, value || undefined);
                                    }}
                                    className="border rounded px-3 py-2 w-full sm:w-auto"
                                />
                            </div>
                        )}

                        {filter.type === "daterange" && (
                            <div className="flex flex-col items-stretch sm:items-center">
                                <InputLabel size={labelSize} bold={boldLabels}>
                                    {filter.label}
                                </InputLabel>
                                {/* Each Input sits in its own flex-1 cell: the Input's inline wrapper
                                    span is not a flex item that can shrink, so two w-full dates in a
                                    row would each take the whole row and the second would run off it. */}
                                <div className="flex gap-2 items-center">
                                    <div className="flex-1 min-w-0 sm:flex-none">
                                        <Input type="date" value={filter.value?.from || ""} onChange={(e) => handleDateRangeChange(filter.id, "from", e.target.value)} placeholder="From" className="border rounded px-3 py-2 w-full sm:w-36" />
                                    </div>
                                    <span className="text-gray-500">to</span>
                                    <div className="flex-1 min-w-0 sm:flex-none">
                                        <Input type="date" value={filter.value?.to || ""} onChange={(e) => handleDateRangeChange(filter.id, "to", e.target.value)} placeholder="To" className="border rounded px-3 py-2 w-full sm:w-36" />
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                ))}

                {activeFilterCount > 0 && (
                    <div className="flex flex-col items-stretch sm:items-center">
                        <div className="h-6"></div> {/* Spacer to align with other inputs */}
                        <button onClick={clearFilters} className="px-3 py-2 bg-gray-100 text-gray-700 rounded hover:bg-gray-200 text-sm font-medium">
                            Clear Filters ({activeFilterCount})
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
}
